import type {
  DistinctVariant,
  MountingBracketGroup,
} from '@product/VariantSelector/MountingBracketSelector/MountingBracketSelector';
import { useSessionStore } from '@hooks/states/useSessionStore';
import { fetchPricesForUnknownProducts } from '@hooks/useIAConfiguratorEvents';
import type { LengthJson } from '@igus/echain-shared-quantities';
import { Length } from '@igus/echain-shared-quantities';
import { AssociationType } from '@libs/associations';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { shoppingCartPosition } from '@libs/catalog-service';
import { getLocale } from '@libs/constants';
import { getInteriorSeparationAssociationProducts, getStandardMountingVariant } from '@libs/echain';
import { processIaConfigEvents } from '@libs/ia-config-events';
import { getAssociationProductReferences, getProductIdForLocale } from '@libs/product.helper';
import type { EchainShoppingCartData, ShoppingCartPositionResponse } from '@models/api/shopping-cart.types';
import type {
  AssociationProductFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import { useShelvesProductAssociationModelsQuery } from '@product/ProductPage/PageLayouts/hooks/useShelvesProductAssociationModelsQuery';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import type { SupportTrayProduct } from '@product/VariantSelector/SupportTraySelector/SupportTraySelector';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useEchainProductStore } from './states/useEchainProductStore';
import { useProductPageStore } from './states/useProductPageStore';
import { useNextQueryParams } from './useNextQueryParams';
import { getPriceCountry } from '@libs/get-price-country.helper';
import { useLengthUnitCodeForUnitSystem } from '@hooks/useLengthUnitCodeForUnitSystem';
import { getEchainDomainLengthUnitByUnitCode } from '@libs/units';

interface MountingBracketConfig {
  mountingVariant?: AssociationProductFragmentEnhanced;
  distinctVariant: DistinctVariant;
  mountingBracketGroup: MountingBracketGroup;
}

interface SupportTrayConfig {
  product: SupportTrayProduct;
  variant: AssociationProductFragmentEnhanced;
}

const getMountingBracketConfigFromArtNr = (
  artNr: string,
  groupedMountingBrackets: MountingBracketGroup[],
  locale: string
): MountingBracketConfig | null => {
  if (artNr === '') {
    return null;
  }

  for (const mountingBracketGroup of groupedMountingBrackets) {
    for (const distinctVariant of mountingBracketGroup.distinctVariants) {
      if (distinctVariant.mountingVariants) {
        for (const mountingVariant of distinctVariant.mountingVariants) {
          if (getProductIdForLocale(mountingVariant, locale) === artNr) {
            return { mountingVariant, distinctVariant, mountingBracketGroup };
          }
        }
      } else if (getProductIdForLocale(distinctVariant, locale) === artNr) {
        const mountingVariant = getStandardMountingVariant(distinctVariant);
        return { mountingVariant, distinctVariant, mountingBracketGroup };
      }
    }
  }
  return null;
};

const getSupportTrayConfigFromArtNr = (
  artNr: string,
  groupedSupportTrays: SupportTrayProduct[],
  locale: string
): SupportTrayConfig | null => {
  if (artNr === '') {
    return null;
  }
  for (const product of groupedSupportTrays) {
    for (const variant of product.products) {
      if (getProductIdForLocale(variant, locale) === artNr) {
        return { product, variant };
      }
    }
  }
  return null;
};

export const useRestoreFromShoppingCartEdit = () => {
  const [responseJson, setResponseJson] = useState<
    ShoppingCartPositionResponse & {
      data?: EchainShoppingCartData;
    }
  >();
  const { edit, i: cartPositionId } = useNextQueryParams() as Record<string, string>; // edit flag for e-chain product page
  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const session = useSessionStore(state => state.session);
  const lengthUnitCode = useLengthUnitCodeForUnitSystem();

  const setIsEdit = useEchainProductStore(state => state.setIsEdit);
  const setEinbauart = useEchainProductStore(state => state.setEinbauart);
  const setEffektiveLaenge = useEchainProductStore(state => state.setEffektiveLaenge);
  const setSystemAmount = useEchainProductStore(state => state.setSystemAmount);
  const setCartPositionId = useEchainProductStore(state => state.setCartPositionId);

  const setSelectedMountingBracketGroup = useEchainProductStore(state => state.setSelectedMountingBracketGroup);
  const setSelectedDistinctVariant = useEchainProductStore(state => state.setSelectedDistinctVariant);
  const setSelectedMountingVariant = useEchainProductStore(state => state.setSelectedMountingVariant);
  const clearSelectedMountingBrackets = useEchainProductStore(state => state.clearSelectedMountingBrackets);
  const setSelectedInteriorSeparator = useEchainProductStore(state => state.setSelectedInteriorSeparator);

  const setSelectedSupportTrayProduct = useEchainProductStore(state => state.setSelectedSupportTrayProduct);
  const setSelectedSupportTrayVariant = useEchainProductStore(state => state.setSelectedSupportTrayVariant);
  const clearSelectedSupportTray = useEchainProductStore(state => state.clearSelectedSupportTray);
  const updateIaConfigItemPricesAndOrderType = useEchainProductStore(
    store => store.updateIaConfigItemPricesAndOrderType
  );
  const updateCablePrices = useEchainProductStore(store => store.updateCablePrices);
  const setIaConfig = useEchainProductStore(state => state.setIaConfig);
  const ketteNumGlieder = useEchainProductStore(store => store.kette?.NumGlieder || 0);
  const groupedMountingBrackets = useEchainProductStore(state => state.groupedMountingBrackets);
  const groupedSupportTrays = useEchainProductStore(state => state.groupedSupportTrays);

  const currentProductId = getProductIdForLocale(currentProduct, locale);
  const priceCountry = useMemo(() => getPriceCountry(locale, session), [locale, session]);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  const { data: shelvesProductAssociationModels, isLoading: isLoadingShelvesAssociationModels } =
    useShelvesProductAssociationModelsQuery();

  const separatorsAssociationProducts = useMemo(
    () =>
      !isLoadingUniqueAssociatedProducts && uniqueAssociatedProducts
        ? getInteriorSeparationAssociationProducts(currentProduct, uniqueAssociatedProducts)
        : [],
    [currentProduct, isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts]
  );

  const unfilteredSeparatorsAssociationProducts = useMemo(
    () =>
      !isLoadingUniqueAssociatedProducts && uniqueAssociatedProducts
        ? getAssociationProductReferences(currentProduct, [AssociationType.SEPARATORS])
            ?.map(associationReference => uniqueAssociatedProducts[getProductIdForLocale(associationReference, locale)])
            .filter(associatedProduct => associatedProduct) || []
        : [],
    [locale, isLoadingUniqueAssociatedProducts, currentProduct, uniqueAssociatedProducts]
  );

  // The unfiltered shelvesProductAssociationModels state prop
  // will be loaded and initiated on the ProductPage component
  const shelvesAssociationProductsFiltered = useMemo(
    () =>
      !isLoadingShelvesAssociationModels && shelvesProductAssociationModels
        ? shelvesProductAssociationModels
            .filter(model =>
              model.products.some(
                product => !getAttributeValue<boolean>(product?.attributes?.mounted_bool as ProductAttribute<boolean>)
              )
            )
            .flatMap(model => model.products)
        : [],
    [isLoadingShelvesAssociationModels, shelvesProductAssociationModels]
  );

  // after config load set echain data
  useEffect(() => {
    const restoreEchainData = () => {
      if (responseJson?.data) {
        const systemAmount = responseJson.quantity;
        if (systemAmount) {
          setSystemAmount(systemAmount);
        }

        const [echain] = responseJson.articles;
        // chain itself
        const { ketteEinbauart } = responseJson.data.shopSettings;
        if (ketteEinbauart) {
          setEinbauart(ketteEinbauart);
        }
        // length needs to restored AFTER einbauart!
        // We don't take the length unit from responseJson.data
        // since it is unit label not unit code. We take unit code from the current unit system.
        const { length } = echain;
        const unit = getEchainDomainLengthUnitByUnitCode(lengthUnitCode);
        if (length) {
          const lengthJson = { Unit: unit, Value: length > 0 ? length : 1 } as LengthJson;
          setEffektiveLaenge(Length.fromJson(lengthJson));
        }
      }
    };
    void restoreEchainData();
  }, [lengthUnitCode, responseJson, setEffektiveLaenge, setEinbauart, setSystemAmount]);

  // restore articles
  useEffect(() => {
    if (responseJson?.articles) {
      const [_, ...articles] = responseJson.articles;

      let mountingBracketConfig: MountingBracketConfig | null = null;
      if (groupedMountingBrackets) {
        for (const article of articles) {
          // check for mountingBrackets
          mountingBracketConfig = getMountingBracketConfigFromArtNr(
            article.articleNumber,
            groupedMountingBrackets,
            locale
          );

          // stop after the first match because we only process a single config anyway
          if (mountingBracketConfig) {
            break;
          }
        }
      }
      if (mountingBracketConfig) {
        setSelectedMountingBracketGroup(mountingBracketConfig.mountingBracketGroup);
        setSelectedDistinctVariant(mountingBracketConfig.distinctVariant);

        if (mountingBracketConfig.mountingVariant) {
          setSelectedMountingVariant(mountingBracketConfig.mountingVariant);
        }
      } else {
        // no mountingBracket => remove default mountingBracket selection
        clearSelectedMountingBrackets(currentProductId);
      }
    }
  }, [
    locale,
    clearSelectedMountingBrackets,
    currentProductId,
    groupedMountingBrackets,
    responseJson?.articles,
    setSelectedDistinctVariant,
    setSelectedMountingBracketGroup,
    setSelectedMountingVariant,
  ]);

  // restore support trays
  useEffect(() => {
    if (responseJson?.articles && groupedSupportTrays && groupedSupportTrays.length > 0) {
      const [_, ...articles] = responseJson.articles;
      let supportTrayConfig: SupportTrayConfig | null = null;
      for (const article of articles) {
        if (groupedSupportTrays && !supportTrayConfig) {
          // check for supportTrays
          supportTrayConfig = getSupportTrayConfigFromArtNr(article.articleNumber, groupedSupportTrays, locale);
        }
      }
      if (supportTrayConfig) {
        setSelectedSupportTrayProduct(supportTrayConfig.product);
        setSelectedSupportTrayVariant(supportTrayConfig.variant);
      } else {
        // no supportTray => remove default supportTray selection
        clearSelectedSupportTray(currentProductId);
      }
    }
  }, [
    locale,
    clearSelectedSupportTray,
    currentProductId,
    responseJson?.articles,
    setSelectedSupportTrayProduct,
    setSelectedSupportTrayVariant,
    groupedSupportTrays,
  ]);

  // restore separators
  useEffect(() => {
    const restoreSeparators = async () => {
      if (responseJson?.articles && separatorsAssociationProducts && separatorsAssociationProducts.length > 0) {
        const [_, ...articles] = responseJson.articles;
        const matchingSeparators: { product: AssociationProductFragmentEnhanced; quantity: number }[] = [];
        for (const article of articles) {
          const separatorsAssociationProduct = separatorsAssociationProducts?.find(
            product => getProductIdForLocale(product, locale) === article.articleNumber
          );
          if (separatorsAssociationProduct) {
            matchingSeparators.push({ product: separatorsAssociationProduct, quantity: article.quantity });
          }
        }

        if (matchingSeparators.length > 0) {
          for (const matchingSeparator of matchingSeparators) {
            await setSelectedInteriorSeparator(
              currentProduct,
              matchingSeparator.product,
              AssociationType.SEPARATORS,
              locale,
              matchingSeparator.quantity
            );
          }
        }
      }
    };
    void restoreSeparators();
  }, [currentProduct, responseJson?.articles, separatorsAssociationProducts, setSelectedInteriorSeparator, locale]);

  // restore shelves
  useEffect(() => {
    const restoreShelves = async () => {
      if (
        responseJson?.articles &&
        shelvesAssociationProductsFiltered &&
        shelvesAssociationProductsFiltered.length > 0
      ) {
        const [_, ...articles] = responseJson.articles;
        const matchingShelves: { product: ModelAssociationProductFragmentEnhanced; quantity: number }[] = [];
        for (const article of articles) {
          const shelfProduct = shelvesAssociationProductsFiltered.find(
            product => getProductIdForLocale(product, locale) === article.articleNumber
          );
          if (shelfProduct) {
            matchingShelves.push({ product: shelfProduct, quantity: article.quantity });
          }
        }

        if (matchingShelves.length > 0) {
          for (const matchingShelf of matchingShelves) {
            await setSelectedInteriorSeparator(
              currentProduct,
              matchingShelf.product,
              AssociationType.SHELVES,
              locale,
              matchingShelf.quantity
            );
          }
        }
      }
    };
    void restoreShelves();
  }, [
    currentProduct,
    responseJson?.articles,
    setSelectedInteriorSeparator,
    shelvesAssociationProductsFiltered,
    locale,
  ]);

  // restore ia-config
  useEffect(() => {
    const restoreIaConfig = async () => {
      if (responseJson?.data?.iaConfiguratorData && responseJson.data.files) {
        const { processedIAConfig: iaConfig, parts } = await processIaConfigEvents(
          {
            action: 'submit',
            payload: {
              configId: responseJson.configurationId,
              exchangeConfiguration: responseJson.data.iaConfiguratorData,
              files: responseJson.data.files,
            },
          },
          ketteNumGlieder,
          unfilteredSeparatorsAssociationProducts,
          shelvesProductAssociationModels,
          locale,
          priceCountry
        );
        if (iaConfig) {
          setIaConfig(iaConfig, parts);

          await fetchPricesForUnknownProducts(parts, priceCountry, updateIaConfigItemPricesAndOrderType);
          updateCablePrices();
        }
      }
    };
    if (responseJson?.data?.iaConfiguratorData && responseJson.data.files) {
      void restoreIaConfig();
    }
  }, [
    ketteNumGlieder,
    locale,
    priceCountry,
    responseJson?.configurationId,
    responseJson?.data?.files,
    responseJson?.data?.iaConfiguratorData,
    setIaConfig,
    shelvesProductAssociationModels,
    unfilteredSeparatorsAssociationProducts,
    updateCablePrices,
    updateIaConfigItemPricesAndOrderType,
  ]);

  // load config from cartPositionId
  useEffect(() => {
    const loadConfigFromPositionId = async () => {
      // get the configuration from shopping cart
      try {
        const response = (await shoppingCartPosition(cartPositionId)) as ShoppingCartPositionResponse & {
          data?: EchainShoppingCartData;
        };

        if (!response.data) {
          throw new Error('Missing data property in ShoppingCartPositionResponse');
        }

        setResponseJson(response);
      } catch (error) {
        Sentry.captureException(new Error(`Restoring data from ShoppingCart went wrong: ${JSON.stringify(error)}`));
      }
    };
    if (cartPositionId && edit) {
      setIsEdit(true);
      setCartPositionId(cartPositionId);

      void loadConfigFromPositionId();
    }
  }, [cartPositionId, edit, setCartPositionId, setIsEdit]);
};
